/**
 * DiscoveryMap Component
 *
 * Shows nearby rooms and user location on the homepage.
 * Allows users to see active rooms in their area.
 */

import { useState, useEffect, useCallback } from 'react'
import { MapContainer, TileLayer, Circle, Marker, Popup, useMap } from 'react-leaflet'
import L from 'leaflet'
import { clsx } from 'clsx'
import { Link } from 'react-router-dom'
import type { GeoLocation } from '@/lib/types'
import type { StoredRoom } from '@/lib/storage'
import { formatDistance } from '@/lib/geo'
import { formatDistanceToNow } from 'date-fns'
import { RoomAccessPopup } from './RoomAccessPopup'

import 'leaflet/dist/leaflet.css'

interface DiscoveryMapProps {
  userLocation: GeoLocation | null
  rooms: StoredRoom[]
  isLoading?: boolean
  onRequestLocation?: () => void
  className?: string
  /** Optional slot for controls (e.g., location accuracy toggle) to render in header */
  headerControls?: React.ReactNode
  /** Callback when user requests access to a room */
  onRequestAccess?: (roomId: string, requesterName: string) => void
}

export function DiscoveryMap({
  userLocation,
  rooms,
  isLoading = false,
  onRequestLocation,
  className,
  headerControls,
  onRequestAccess,
}: DiscoveryMapProps) {
  const [isExpanded, setIsExpanded] = useState(true)
  const [selectedRoom, setSelectedRoom] = useState<StoredRoom | null>(null)

  // Default center (will be overridden by user location or first room)
  const defaultCenter: GeoLocation = userLocation ??
    (rooms[0] ? { latitude: rooms[0].centerLat, longitude: rooms[0].centerLng } :
      { latitude: 40.7128, longitude: -74.006 }) // NYC fallback

  const handleRoomSelect = useCallback((room: StoredRoom) => {
    setSelectedRoom(room)
  }, [])

  const handleClosePopup = useCallback(() => {
    setSelectedRoom(null)
  }, [])

  return (
    <div className={clsx('relative', className)}>
      {/* Header */}
      <div
        className={clsx(
          'w-full flex items-center justify-between px-3 py-2',
          'bg-black/40 border border-white/10 rounded-t-lg',
          !isExpanded && 'rounded-b-lg'
        )}
      >
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 transition-colors hover:opacity-80"
        >
          <span className="text-xs font-bold uppercase tracking-widest text-primary">Map // Nearby Rooms</span>
          {rooms.length > 0 && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary/20 text-primary border border-primary/30">
              {rooms.length} DETECTED
            </span>
          )}
        </button>
        <div className="flex items-center gap-2">
          {headerControls}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-white/40 text-xs font-mono hover:text-white/60 transition-colors"
          >
            {isExpanded ? '[ COLLAPSE ]' : '[ EXPAND ]'}
          </button>
        </div>
      </div>

      {/* Map container */}
      {isExpanded && (
        <div className="border border-t-0 border-white/10 rounded-b-lg overflow-hidden relative">
          {/* Location permission prompt */}
          {!userLocation && !isLoading && (
            <div className="absolute inset-0 z-[1000] bg-black/90 flex flex-col items-center justify-center p-4 backdrop-blur-sm">
              <div className="text-4xl mb-3 opacity-50">📍</div>
              <p className="text-white/80 text-center mb-4 text-xs font-mono uppercase tracking-wide">
                Location Services Offline
              </p>
              <button
                onClick={onRequestLocation}
                className="px-4 py-2 bg-primary/20 text-primary border border-primary/50 rounded font-bold uppercase tracking-widest hover:bg-primary/30 hover:shadow-[0_0_15px_rgba(242,13,128,0.3)] transition-all text-xs"
              >
                Initialize GPS
              </button>
            </div>
          )}

          {/* Loading state */}
          {isLoading && (
            <div className="absolute inset-0 z-[1000] bg-black/90 flex items-center justify-center backdrop-blur-sm">
              <div className="text-center">
                <div className="text-2xl mb-2 animate-pulse text-primary">📡</div>
                <p className="text-white/60 text-xs font-mono uppercase tracking-wider">Triangulating Position...</p>
              </div>
            </div>
          )}

          <div className="h-48 md:h-64 relative">
            <div className="absolute inset-0 pointer-events-none z-[400] border border-white/5 box-border">
              <div className="absolute top-2 left-2 text-[8px] font-mono text-white/30">SAT_VIEW_Active</div>
              <div className="absolute bottom-2 right-2 text-[8px] font-mono text-white/30">GRID_REF_X99</div>
            </div>
            <MapContainer
              center={[defaultCenter.latitude, defaultCenter.longitude]}
              zoom={14}
              className="w-full h-full bg-[#0a0a0a]"
              zoomControl={false}
              attributionControl={false}
            >
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
              />

              {/* User location */}
              {userLocation && (
                <UserLocationMarker location={userLocation} />
              )}

              {/* Room markers */}
              {rooms.map((room) => (
                <RoomMarker
                  key={room.id}
                  room={room}
                  userLocation={userLocation}
                  onSelect={handleRoomSelect}
                />
              ))}

              {/* Auto-fit bounds */}
              <MapBoundsHandler
                userLocation={userLocation}
                rooms={rooms}
              />
            </MapContainer>
          </div>

          {/* Room list */}
          {rooms.length > 0 && (
            <div className="px-3 py-2 bg-black/60 border-t border-white/10 max-h-32 overflow-y-auto backdrop-blur-sm">
              <div className="space-y-1">
                {rooms.map((room) => (
                  <RoomListItem
                    key={room.id}
                    room={room}
                    userLocation={userLocation}
                    onSelect={handleRoomSelect}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {rooms.length === 0 && userLocation && (
            <div className="px-3 py-6 bg-black/40 border-t border-white/10 text-center">
              <p className="text-white/40 text-[10px] font-mono uppercase tracking-wider mb-2">
                // No active signals in sector //
              </p>
              <Link
                to="/create"
                className="inline-block px-4 py-2 bg-primary/20 text-primary border border-primary/50 text-xs rounded font-bold uppercase tracking-widest hover:bg-primary/30 transition-all hover:shadow-[0_0_10px_rgba(242,13,128,0.2)]"
              >
                Establish New Link
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Room Access Popup */}
      {selectedRoom && (
        <RoomAccessPopup
          room={selectedRoom}
          userLocation={userLocation}
          onClose={handleClosePopup}
          onRequestAccess={onRequestAccess}
        />
      )}
    </div>
  )
}

/**
 * User location marker
 */
function UserLocationMarker({ location }: { location: GeoLocation }) {
  const userIcon = L.divIcon({
    className: 'user-marker',
    html: `
      <div style="
        width: 14px;
        height: 14px;
        background: #3b82f6;
        border: 2px solid white;
        border-radius: 50%;
        box-shadow: 0 0 15px rgba(59, 130, 246, 0.8), 0 0 5px rgba(59, 130, 246, 1);
      "></div>
    `,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  })

  return (
    <>
      {/* Accuracy circle */}
      {location.accuracy && location.accuracy < 1000 && (
        <Circle
          center={[location.latitude, location.longitude]}
          radius={location.accuracy}
          pathOptions={{
            color: '#3b82f6',
            fillColor: '#3b82f6',
            fillOpacity: 0.05,
            weight: 1,
            dashArray: '4, 4'
          }}
        />
      )}
      <Marker
        position={[location.latitude, location.longitude]}
        icon={userIcon}
      >
        <Popup className="tactical-popup">
          <div className="text-xs font-mono font-bold uppercase tracking-wide bg-black text-white p-1">
            YOU
          </div>
        </Popup>
      </Marker>
    </>
  )
}

/**
 * Room marker on the map
 */
function RoomMarker({
  room,
  userLocation,
  onSelect,
}: {
  room: StoredRoom
  userLocation: GeoLocation | null
  onSelect: (room: StoredRoom) => void
}) {
  const roomIcon = L.divIcon({
    className: 'room-marker',
    html: `
      <div style="
        width: 28px;
        height: 28px;
        background: rgba(242, 13, 128, 0.9);
        border: 2px solid white;
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        box-shadow: 0 0 15px rgba(242, 13, 128, 0.6);
        transform: rotate(45deg);
      ">
        <div style="transform: rotate(-45deg);">📻</div>
      </div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  })

  // Calculate distance
  let distanceText = ''
  if (userLocation) {
    const R = 6371000
    const dLat = (room.centerLat - userLocation.latitude) * Math.PI / 180
    const dLon = (room.centerLng - userLocation.longitude) * Math.PI / 180
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(userLocation.latitude * Math.PI / 180) * Math.cos(room.centerLat * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    const distance = R * c
    distanceText = formatDistance(distance)
  }

  return (
    <>
      {/* Geofence circle */}
      <Circle
        center={[room.centerLat, room.centerLng]}
        radius={room.radiusMeters}
        pathOptions={{
          color: '#f20d80',
          fillColor: '#f20d80',
          fillOpacity: 0.1,
          weight: 1,
          dashArray: '5, 10'
        }}
      />
      <Marker
        position={[room.centerLat, room.centerLng]}
        icon={roomIcon}
      >
        <Popup>
          <div className="min-w-[160px] p-1">
            <div className="font-bold text-xs uppercase tracking-wider text-primary mb-1 border-b border-primary/20 pb-1">{room.name}</div>
            <div className="text-black/70 text-[10px] font-mono">
              RANGE: {room.radiusMeters}m
              {distanceText && ` | D: ${distanceText}`}
            </div>
            <div className="text-black/50 text-[10px] uppercase mt-1">
              Active {formatDistanceToNow(room.createdAt, { addSuffix: true })}
            </div>
            <button
              onClick={() => onSelect(room)}
              className="block w-full mt-2 px-3 py-1.5 bg-primary text-white text-[10px] text-center font-bold uppercase tracking-widest hover:bg-primary/90 cursor-pointer"
            >
              UPLINK
            </button>
          </div>
        </Popup>
      </Marker>
    </>
  )
}

/**
 * Room list item below the map
 */
function RoomListItem({
  room,
  userLocation,
  onSelect,
}: {
  room: StoredRoom
  userLocation: GeoLocation | null
  onSelect: (room: StoredRoom) => void
}) {
  // Calculate distance
  let distanceText = ''
  if (userLocation) {
    const R = 6371000
    const dLat = (room.centerLat - userLocation.latitude) * Math.PI / 180
    const dLon = (room.centerLng - userLocation.longitude) * Math.PI / 180
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(userLocation.latitude * Math.PI / 180) * Math.cos(room.centerLat * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    const distance = R * c
    distanceText = formatDistance(distance)
  }

  return (
    <button
      onClick={() => onSelect(room)}
      className="w-full flex items-center justify-between p-2 rounded border border-transparent hover:bg-white/5 hover:border-white/10 transition-colors group text-left"
    >
      <div className="flex items-center gap-3">
        <span className="text-lg group-hover:animate-pulse">📻</span>
        <div>
          <div className="text-xs font-bold text-white uppercase tracking-wide group-hover:text-primary transition-colors">{room.name}</div>
          <div className="text-[10px] text-white/40 font-mono">
            {distanceText && `${distanceText} • `}
            {room.radiusMeters}m radius
          </div>
        </div>
      </div>
      <span className="text-white/40 text-xs font-mono group-hover:text-primary">→</span>
    </button>
  )
}

/**
 * Auto-adjust map bounds
 */
function MapBoundsHandler({
  userLocation,
  rooms,
}: {
  userLocation: GeoLocation | null
  rooms: StoredRoom[]
}) {
  const map = useMap()

  useEffect(() => {
    if (!userLocation && rooms.length === 0) return

    const bounds = L.latLngBounds([])

    if (userLocation) {
      bounds.extend([userLocation.latitude, userLocation.longitude])
    }

    rooms.forEach((room) => {
      bounds.extend([room.centerLat, room.centerLng])
      // Include geofence radius
      const latOffset = room.radiusMeters / 111000
      const lngOffset = room.radiusMeters / (111000 * Math.cos(room.centerLat * Math.PI / 180))
      bounds.extend([room.centerLat + latOffset, room.centerLng + lngOffset])
      bounds.extend([room.centerLat - latOffset, room.centerLng - lngOffset])
    })

    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 })
    }
  }, [map, userLocation, rooms])

  return null
}
